import {useState, useCallback, useContext, useEffect} from "react";
import {paymentsAPI} from "../common/api/paymentsAPI";
import {
    API_BOOKING,
    API_INITIALIZE_PAYMENT,
    API_INVOICES,
    API_SAFERPAY
} from "../common/api/apiConstants";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {UserContext} from "../store/providers/UserProvider";
import {INVOICE_CURRENCY} from "../common/constant";
import {useUserAccounts} from "./useUserAccounts";
import {LocaleContext} from "../store/providers/LocaleProvider";
import {checkEnableAnnualSubscriptionInvoice, checkEnableCreditCardForUsMembersWithChfCurrency} from "../common/configcat/functions";

const INVOICE_TYPES = {
    others: "others",
    services: "services",
    payments: "payments",
    product_repurchase: "product_repurchase",
    annual_subscription: "annual_subscription",
    product_purchase: "product_purchase",
    points_sale: "points_sale",
    points_purchase: "points_purchase"
};


export const usePayments = () => {
    const {localeUppercaseUnderscore} = useContext(LocaleContext)[0];
    const {initialized, authenticated} = useKeycloakFunctions();
    const {user, loadingUser} = useContext(UserContext)?.[0];
    const {getBookingStatus} = useUserAccounts();

    const [ready, setReady] = useState(false);
    const [initializeBookingPaymentData, setInitializeBookingPaymentData] = useState();
    const [loadingInitializeBookingPayment, setLoadingInitializeBookingPayment] = useState(false);
    const [errorInitializeBookingPayment, setErrorInitializeBookingPayment] = useState(false);

    const [invoices, setInvoices] = useState();
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [errorInvoices, setErrorInvoices] = useState(false);

    const [initializeInvoicePaymentData, setInitializeInvoicePaymentData] = useState();
    const [loadingInitializeInvoicePayment, setLoadingInitializeInvoicePayment] = useState(false);
    const [errorInitializeInvoicePayment, setErrorInitializeInvoicePayment] = useState(false);

    const [isUsMembersWithChfCurrencyEnabled, setIsUsMembersWithChfCurrencyEnabled] = useState(false);
    const [isAnnualChargeInvoiceEnabled, setIsAnnualChargeInvoiceEnabled] = useState(false);

    useEffect(() => {
        Promise.all([
            checkEnableCreditCardForUsMembersWithChfCurrency(),
            checkEnableAnnualSubscriptionInvoice()
        ]).then(([isUsMembersWithChfCurrencyEnabled, isAnnualChargeInvoiceEnabled]) => {
            setIsUsMembersWithChfCurrencyEnabled(isUsMembersWithChfCurrencyEnabled);
            setIsAnnualChargeInvoiceEnabled(isAnnualChargeInvoiceEnabled);
            setReady(true);
        });
    }, []);

    const initializeBookingPayment = useCallback((reservation_id) => {
        setLoadingInitializeBookingPayment(true);
        setErrorInitializeBookingPayment(null);
        if (initialized && authenticated) {
            const payload = {
                order_id: reservation_id,
                locale: localeUppercaseUnderscore
            };

            return paymentsAPI.post(API_BOOKING + API_SAFERPAY + API_INITIALIZE_PAYMENT, payload)
                .then(res => {
                    const data = res?.data;
                    if (data) {
                        setInitializeBookingPaymentData(data);
                    }
                    return data;
                })
                .catch(err => {
                    setErrorInitializeBookingPayment(err?.response);
                    throw err;
                })
                .finally(() => setLoadingInitializeBookingPayment(false));
        }
    }, [initialized, authenticated]);

    const getInvoices = useCallback(() => {
        const calculateInvoiceCurrency = (invoices, isAccountBlocked) => {
            /*
            * If there is at least one record with 4 reminders,
            * and user's account is blocked use
            * always invoices currency
            */
            if (!isAccountBlocked &&
                user?.systemCurrency !== INVOICE_CURRENCY &&
                invoices?.every((inv) => Boolean(inv?.amount?.find(a => a?.currency === user?.systemCurrency)))
            ) {
                return user?.systemCurrency;
            }

            return INVOICE_CURRENCY;
        };

        const calculateTotals = (invoices, currency = INVOICE_CURRENCY) => {
            let totalCredit = 0;
            let totalDebit = 0;
            let totalProfit = 0;
            let totalBalance = 0;

            // Calculate debit and credit from invoice records
            for (const inv of invoices?.filter(inv => inv?.invoice_type !== "points_sale")) {
                if (inv?.is_credit) {
                    totalCredit += inv?.amount?.find((am) => am?.currency === currency)?.value;
                } else {
                    totalDebit += inv?.amount?.find((am) => am?.currency === currency)?.value;
                }
            }

            // Calculate profit from points sale records
            for (const inv of invoices?.filter(inv => inv?.invoice_type === "points_sale")) {
                totalProfit += inv?.amount?.find((am) => am?.currency === currency)?.value;
            }

            totalProfit = Number.isInteger(totalProfit) ? totalProfit : totalProfit?.toFixed(2),
            totalCredit = Number.isInteger(totalCredit) ? totalCredit : totalCredit?.toFixed(2),
            totalDebit = Number.isInteger(totalDebit) ? totalDebit : totalDebit?.toFixed(2),
            totalBalance = totalCredit - totalDebit;

            return {
                totalProfit,
                totalCredit,
                totalDebit,
                totalBalance: Number.isInteger(totalBalance) ? totalBalance : totalBalance?.toFixed(2)
            };
        };

        setLoadingInvoices(true);
        setErrorInvoices(null);
        if (initialized && authenticated && !loadingUser && user) {
            return paymentsAPI.get(API_INVOICES)
                .then(async res => {
                    const data = res?.data;
                    if (data) {
                        // Hide annual charges invoice for new year(2024) based on feature flag
                        // TODO remove when not needed anymore
                        const initialInvoicePool = !isAnnualChargeInvoiceEnabled 
                            ? data?.filter(item => {
                                if (item?.invoice_type === INVOICE_TYPES.annual_subscription) {
                                    if (new Date(item?.creation_date)?.getFullYear() === 2024) {
                                        return false;
                                    }
                                } 
                                return true;
                            }) : data;

                        // account is blocked if user has 4 reminders in at least one invoice
                        const bookingStop = (await getBookingStatus(user?.member_id))?.booking_stop;
                        const accountBlocked = initialInvoicePool?.some((inv) => inv?.dunning_level >= 4);
                        const currency = calculateInvoiceCurrency(initialInvoicePool, accountBlocked);
                        const totals = calculateTotals(initialInvoicePool, currency);

                        const docs = initialInvoicePool
                            ?.map((inv) => {
                                return {
                                    ...inv,
                                    title: inv?.title?.startsWith("*") ? inv?.title?.substr(1) : inv?.title,
                                    amount: inv?.amount?.find((am) => am?.currency === currency)?.value,
                                    currency: currency
                                };
                            });

                        const tmpInvoices = {
                            totalProfit: totals?.totalProfit,
                            totalCredit: totals?.totalCredit,
                            totalDebit: totals?.totalDebit,
                            totalBalance: totals?.totalBalance,
                            currency,
                            accountBlocked,
                            bookingStop,
                            invoiceDocs: docs?.filter((inv) => inv?.invoice_type !== "points_sale"),
                            pointsSalesDocs: docs?.filter((inv) => inv?.invoice_type === "points_sale")
                        };
                        setInvoices(tmpInvoices);

                        return initialInvoicePool;
                    }
                })
                .catch(err => {
                    setErrorInvoices(err?.response);
                })
                .finally(() => setLoadingInvoices(false));
        }
    }, [initialized, authenticated, loadingUser, user, isAnnualChargeInvoiceEnabled]);

    const initializeInvoicePayment = useCallback((invoice_id, redirectToPaymentPortal = false) => {
        setLoadingInitializeInvoicePayment(true);
        setErrorInitializeInvoicePayment(null);
        if (initialized && authenticated) {
            const payload = {
                order_id: invoice_id,
                locale: localeUppercaseUnderscore
            };

            return paymentsAPI.post(API_INVOICES + API_SAFERPAY + API_INITIALIZE_PAYMENT, payload)
                .then(res => {
                    const data = res?.data;
                    if (data) {
                        setInitializeInvoicePaymentData(data);
                        if (redirectToPaymentPortal) {
                            location.assign(data?.redirect_url);
                        }
                    }
                    return data;
                })
                .catch(err => {
                    setErrorInitializeInvoicePayment(err);
                    throw err;
                })
                .finally(() => setLoadingInitializeInvoicePayment(false));
        }
    }, [initialized, authenticated]);

    /*
        User can pay invoice only if currency is CHF and
        country is NOT CH or LI or US
    */
    const isInvoicePaymentAvailable = useCallback(() => {
        if (user?.systemCurrency === "CHF"
            && user?.country !== "ch"
            && user?.country !== "li"
            && (isUsMembersWithChfCurrencyEnabled || user?.country !== "us")
        ) {
            return true;
        } else {
            return false;
        }
    }, [user, loadingUser, isUsMembersWithChfCurrencyEnabled]);

    return {
        ready,

        initializeBookingPayment,
        initializeBookingPaymentData,
        loadingInitializeBookingPayment,
        errorInitializeBookingPayment,

        invoices,
        loadingInvoices,
        errorInvoices,
        getInvoices,

        initializeInvoicePayment,
        isInvoicePaymentAvailable,
        initializeInvoicePaymentData,
        loadingInitializeInvoicePayment,
        errorInitializeInvoicePayment
    };
};
